import {
  Button,
  DomElementAlignment,
  DomTargetPosition,
  DynamicForm,
  Loader,
  OdinIcon,
  OdinIconSize,
  OverlayPanel,
  Tooltip,
  useScreenSize,
} from '@myosh/odin-components';
import type {
  DynamicFormButtonSetting,
  DynamicFormRef,
  DynamicFormSettings,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrieval,
  OdinDataRetrievalOptions,
  OdinDataSender,
  OdinDataUpdate,
} from '@myosh/odin-components';
import clsx from 'clsx';
import { cloneDeep, isArray } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ComponentProps } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';
import type {
  GlobalHierarchyFilters,
  HierarchyDropdownValue,
  HierarchyMatchingOptionDropDownValue,
} from '../../@types/hierarchy-fields';
import i18next from '../../i18n';
import { saveButton } from '../../pages/admin/admin-utils';
import { useAppDispatch } from '../../redux/hooks';
import {
  useDeleteGlobalHierarchyFiltersMutation,
  useGetGlobalHierarchyFiltersQuery,
  useHierarchyTypesQuery,
  useUpdateGlobalHierarchyFiltersMutation,
} from '../../redux/services/hierarchy';
import type { DataObject } from '../../redux/slices/global-hierarchies';
import {
  resetGlobalHierarchyFilters,
  setMatchingOption,
  setSelectedHierarchies,
} from '../../redux/slices/global-hierarchies';
import { promiseToast } from '../../services/notification.service';
import { createGlobalHierarchyFiltersPatch } from '../../utils/hierarchy-functions';
import { forceAssert } from '../../utils/type-guards';
import { HfHierarchyField } from '../fields/hierarchy/hf-hierarchy-field.component';

const resetButton: DynamicFormButtonSetting = {
  name: 'reset',
  text: i18next.t('reset'),
  htmlType: 'button',
  type: 'default',
  variant: 'alternative',
};

const optionGroupOptions = [
  {
    value: 'MATCH_ANY',
    text: i18next.t('match-any-hierarchy-values', { any: `<strong>${i18next.t('any').toLowerCase()}</strong>` }),
  },
  {
    value: 'MATCH_ALL',
    text: i18next.t('match-all-hierarchy-values', { all: `<strong>${i18next.t('all').toLowerCase()}</strong>` }),
  },
];

const GlobalHierarchiesFilter = ({ className, ...props }: ComponentProps<'div'>) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const [isOverlayShown, setIsOverlayShown] = useState(false);
  const [formButtons, setFormButtons] = useState<DynamicFormButtonSetting[]>();
  const [globalHierarchiesFilterData, setGlobalHierarchiesFilterData] = useState<GlobalHierarchyFilters>();

  const dynamicFormReference = useRef<DynamicFormRef>(null);
  const targetRef = useRef<HTMLDivElement>(null);
  const dynamicFormId = useRef(v4());
  const submittingRef = useRef(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { isMobile } = useScreenSize();

  const { data: hierarchyTypes } = useHierarchyTypesQuery({ archived: 'false' });
  const { data } = useGetGlobalHierarchyFiltersQuery();
  const [updateGlobalHierarchies, { isLoading: isSavingGlobalHierarchies }] = useUpdateGlobalHierarchyFiltersMutation();
  const [resetGlobalHierarchies, { isLoading: isResettingGlobalHierarchies }] =
    useDeleteGlobalHierarchyFiltersMutation();

  const isUpdating = isSavingGlobalHierarchies || isResettingGlobalHierarchies;

  useEffect(() => {
    if (data) {
      setGlobalHierarchiesFilterData(data);
      const newFormButtons: Array<DynamicFormButtonSetting> = [];

      const _resetButton = cloneDeep(resetButton);
      _resetButton.onClick = () => onFormReset();
      _resetButton.htmlType = 'button';

      const _saveButton = cloneDeep(saveButton);
      _saveButton.onClick = () => onFormSubmit();
      _saveButton.htmlType = 'button';

      newFormButtons.push(_saveButton, _resetButton);
      setFormButtons(newFormButtons);
    }

    // Handle global state for global hierarchy filters
    if (data && data.globalHierarchyFilters) {
      const listOfActiveHierarchyTypes =
        !isArray(data.globalHierarchyFilters) &&
        Object.keys(data.globalHierarchyFilters as Record<string, HierarchyDropdownValue[]>);
      if (isArray(listOfActiveHierarchyTypes)) {
        let dispatchData: DataObject[] = [];
        for (let i = 0, length = listOfActiveHierarchyTypes.length; i < length; i++) {
          const filterData = forceAssert<Record<string, HierarchyDropdownValue[]>>(data.globalHierarchyFilters)[
            listOfActiveHierarchyTypes[i]
          ];
          const formatFilterData = filterData.map((filter) => {
            return {
              id: filter.id,
              typeId: filter.hierarchyTypeId,
            };
          });
          dispatchData = [...dispatchData, ...formatFilterData];
        }

        const groupedData: Record<number, number[]> = {};

        for (let i = 0, length = dispatchData.length; i < length; i++) {
          const globalFilterData = dispatchData[i];
          if (!groupedData[globalFilterData.typeId]) {
            groupedData[globalFilterData.typeId] = [];
          }
          groupedData[globalFilterData.typeId].push(globalFilterData.id);
        }
        dispatch(setSelectedHierarchies(groupedData));

        const globalFiltersMatchingOption =
          forceAssert<HierarchyMatchingOptionDropDownValue>(data?.hierarchyMatchingOption).value === 'MATCH_ALL'
            ? 'all'
            : 'any';
        dispatch(setMatchingOption(globalFiltersMatchingOption));
      }
    }
  }, [data]);

  const hierarchyGlobalFiltersFormSettings: DynamicFormSettings = useMemo(() => {
    return {
      id: 0,
      caption: '',
      fields: [
        {
          id: 'globalHierarchyFilters',
          fieldType: 'CUSTOM',
          customFieldType: 'HIERARCHY_ADMIN',
          caption: t('hierarchy'),
          fieldName: 'globalHierarchyFilters',
          position: 0,
          permissions: { read: true, edit: true },
          dynamicProperties: {
            hierarchyTypes: hierarchyTypes,
            usesMultiSelectDropdowns: true,
          },
          wrapInGroup: false,
          hidden: false,
        },
        {
          id: 'hierarchyMatchingOption',
          fieldType: 'OPTIONGROUP',
          fieldName: 'hierarchyMatchingOption',
          caption: '',
          position: 1,
          permissions: { read: true, edit: true },
          dataSettings: {
            valueField: 'value',
            textField: 'text',
          },
        },
      ],
      customFieldComponents: {
        HIERARCHY_ADMIN: HfHierarchyField,
      },
    };
  }, [hierarchyTypes, t]);

  const toggleHierarchyDropdown = () => {
    setIsOverlayVisible((prev) => !prev);
  };

  const dataRetrieval: OdinDataRetrieval & OdinDataUpdate<JsonDataWrapper> = {
    getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
      if (
        options &&
        options.fieldType === 'OPTIONGROUP' &&
        options.fieldId === 'hierarchyMatchingOption' &&
        options.page === 1
      ) {
        subscriber.sendData({ data: optionGroupOptions, requestId: options.requestId });
      } else {
        subscriber.sendData();
      }
    },
  };

  const onFormReset = () => {
    if (submittingRef.current || isUpdating) return;
    submittingRef.current = true;
    promiseToast(
      resetGlobalHierarchies()
        .unwrap()
        .then(() => {
          dispatch(resetGlobalHierarchyFilters());
          setIsOverlayVisible(false);
        }),
      {
        loading: t('performing-save'),
        success: t('global-hierarchies-reset'),
        error: t('global-hierarchies-reset-failed'),
      },
      { id: 'global-hierarchy-filter-reset' }
    ).finally(() => {
      submittingRef.current = false;
    });
  };

  const onFormSubmit = () => {
    if (submittingRef.current || isUpdating) return;
    submittingRef.current = true;

    const data = dynamicFormReference.current?.getData() as JsonDataItem;
    setGlobalHierarchiesFilterData(data);
    const patchData = createGlobalHierarchyFiltersPatch(data);
    promiseToast(
      updateGlobalHierarchies(patchData)
        .unwrap()
        .then(() => {
          setIsOverlayVisible(false);
        }),
      {
        success: t('global-hierarchies-saved'),
        error: t('global-hierarchies-save-failed'),
        loading: t('performing-save'),
      },
      { id: 'global-hierarchy-filter-save' }
    ).finally(() => {
      submittingRef.current = false;
    });
  };

  const onOverlayHidden = () => {
    setIsOverlayVisible(false);
    setIsOverlayShown(false);
  };

  const onOverlayShown = () => {
    // needed to mask the dynamic form layout jumping when it renders the fields
    setTimeout(() => setIsOverlayShown(true), 1000);
  };

  const activeHierarchyFilters = useMemo(() => {
    if (isMobile) {
      return <OdinIcon icon="Globe" size={OdinIconSize.Small} className="cursor-pointer rounded-md text-primary-1" />;
    } else if (globalHierarchiesFilterData && globalHierarchiesFilterData.globalHierarchyFilters) {
      return (
        <div className="flex flex-row justify-items-center whitespace-nowrap px-4">
          <div className="pr-1 text-center text-sm font-bold">{t('global-hierarchy')}:</div>
          <div className="text-center text-sm font-normal text-primary-1">
            {getGlobalHierarchyFiltersDisplayText(globalHierarchiesFilterData.globalHierarchyFilters)}
          </div>
        </div>
      );
    }
  }, [globalHierarchiesFilterData, isMobile, t]);

  const formButtonsComponent = () => {
    return (
      <div className="odin-border-t odin-border-solid odin-border-gray-4 absolute bottom-0 left-0 flex w-full max-w-full items-center gap-2 bg-mono-1 p-4">
        {formButtons?.map((button, index) => (
          <Button
            key={`button_${button.name}_${index}`}
            htmlType={button.htmlType}
            type={button.type}
            variant={button.variant}
            classNames={button.className}
            disabled={isUpdating}
            onClick={() => button.onClick?.()}
          >
            {button.text}
          </Button>
        ))}
      </div>
    );
  };

  return (
    <div className={clsx(className, 'flex flex-shrink-0 flex-row items-center')} {...props}>
      <div ref={targetRef} onClick={toggleHierarchyDropdown} className="cursor-pointer">
        {activeHierarchyFilters}
      </div>
      <OverlayPanel
        target={targetRef.current}
        visible={isOverlayVisible}
        hidden={onOverlayHidden}
        elementAlignment={DomElementAlignment.TopLeft}
        targetPosition={DomTargetPosition.BottomLeft}
        shouldScrollCloseOverlay={false}
        mountOnEnter={true}
        unmountOnExit={true}
        shouldCheckZIndex={true}
        shouldUseCreatePortal={false}
        className="h-[500px] w-64 max-w-lg bg-mono-1 p-4 text-mono-0 sm:w-96"
        shouldRemainOnScreen={true}
        showCloseIcon={true}
        shown={onOverlayShown}
      >
        <div
          className="absolute z-[1000] -ml-4 -mt-4 flex h-full w-full items-center justify-center bg-mono-1"
          hidden={isOverlayShown}
        >
          <Loader />
        </div>
        <div className="custom-scroll max-h-[420px] overflow-y-auto pt-2">
          <DynamicForm
            ref={dynamicFormReference}
            dynamicFormId={dynamicFormId.current}
            data={forceAssert<JsonDataItem>(cloneDeep(globalHierarchiesFilterData)) ?? {}}
            settings={hierarchyGlobalFiltersFormSettings}
            dataRetrieval={dataRetrieval}
            onSubmit={onFormSubmit}
            showSubmitButtons={false}
          />
        </div>

        {formButtonsComponent()}
      </OverlayPanel>
    </div>
  );
};

export default GlobalHierarchiesFilter;

// util
const getGlobalHierarchyFiltersDisplayText = (selectedHierarchies: Record<string, HierarchyDropdownValue[]>) => {
  if (isArray(selectedHierarchies)) {
    return <div className="text-sm font-semibold text-mono-1">{`${i18next.t('all')}`}</div>;
  } else {
    const keys = Object.keys(selectedHierarchies).filter((key) => selectedHierarchies[key].length > 0);

    const tooltipDescription = keys.map((key) => (
      <div key={key} className="text-sm font-normal">
        {`${key}(${selectedHierarchies[key].map((h) => h.caption).join(', ')})`}
      </div>
    ));

    if (keys.length > 2) {
      return (
        <Tooltip
          description={tooltipDescription}
          tooltipClassName="bg-mono-1"
          debounceTime={200}
          tooltipAlignment={DomElementAlignment.TopLeft}
        >
          <div className="text-sm font-normal text-mono-1">{`${i18next.t('multiple')}(${keys.length})`}</div>
        </Tooltip>
      );
    } else {
      const displayText = keys
        .map((key) => {
          return `${key}(${selectedHierarchies[key].length})`;
        })
        .join(' ');

      return (
        <Tooltip
          description={tooltipDescription}
          tooltipClassName="bg-mono-1"
          debounceTime={200}
          tooltipAlignment={DomElementAlignment.TopLeft}
        >
          <div className="text-sm font-normal text-mono-1">{displayText}</div>
        </Tooltip>
      );
    }
  }
};
